<script setup lang="ts">
// biome-ignore lint/correctness/noUnusedImports: required for <l-map> / <l-tile-layer> in template
import { LMap, LTileLayer } from "@vue-leaflet/vue-leaflet";
import { Icon } from "leaflet";
import { MarkerClusterGroup } from "leaflet.markercluster";
import "leaflet.markercluster/dist/MarkerCluster.css";
import "leaflet.markercluster/dist/MarkerCluster.Default.css";
import { computed, nextTick, useTemplateRef, watch } from "vue";

import "leaflet/dist/leaflet.css";

delete Icon.Default.prototype._getIconUrl;

interface ShortCoordinates {
    lat: number;
    lng: number;
}

interface Coordinates {
    latitude?: number;
    longitude?: number;
}

interface Row extends Coordinates {
    id: string | number;
    name: string;
}

interface MapViewProps {
    zoom?: number;
    rows?: Row[];
    border?: boolean;
}

const props = withDefaults(defineProps<MapViewProps>(), {
    zoom: 6,
    border: true,
});

const emit = defineEmits<(event: "markerClick", id: string | number) => void>();

// biome-ignore lint/suspicious/noExplicitAny: Init Biome
const map: any = useTemplateRef("map");

const center = computed<ShortCoordinates>(() => {
    if (props.rows?.length && props.rows[0].latitude && props.rows[0].longitude) {
        return { lat: props.rows[0].latitude, lng: props.rows[0].longitude };
    }
    return { lat: 46.977391, lng: 0.6928313 };
});

const url: string = "https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png";

const attribution: string = '<a target="_blank" href="http://osm.org/copyright">OpenStreetMap</a> contributors';

// Universal function to extract [lat, lng] from a Row
// biome-ignore lint/suspicious/noExplicitAny: Init Biome
const getLatLng = (row: any): [number, number] | null => {
    // Format 1 : { latitude, longitude }
    if (typeof row.latitude === "number" && typeof row.longitude === "number") {
        return [row.latitude, row.longitude];
    }
    // Format 2 : { location: [lng, lat] }
    if (Array.isArray(row.location) && row.location.length === 2) {
        return [row.location[1], row.location[0]];
    }
    // Format 3 : { address: { location: [lng, lat] } }
    if (row.address && Array.isArray(row.address.location) && row.address.location.length === 2) {
        return [row.address.location[1], row.address.location[0]];
    }
    return null;
};

const boundMap = async () => {
    if (!window.L || !map.value?.leafletObject) return;

    const L = window.L;

    // biome-ignore lint/suspicious/noExplicitAny: Init Biome
    map.value.leafletObject.eachLayer((layer: any) => {
        if (layer instanceof L.MarkerClusterGroup) {
            map.value.leafletObject.removeLayer(layer);
        }
    });

    if (!props.rows?.length) return;

    const markerClusterGroup = new MarkerClusterGroup();

    map.value.leafletObject.addLayer(markerClusterGroup);

    const markers = (props.rows || [])
        // biome-ignore lint/suspicious/noExplicitAny: Init Biome
        .map((row: any) => {
            const latlng = getLatLng(row);

            if (latlng) {
                const marker = new L.Marker(latlng);
                const popup = new L.Popup();
                popup.setContent(row.name || row.label || "");
                marker.bindPopup(popup);
                if (row.id !== undefined && row.id !== null) {
                    marker.on("click", () => emit("markerClick", row.id));
                }
                return marker;
            }
            return null;
        })
        .filter(Boolean);

    markerClusterGroup.addLayers(markers);
};

watch(
    () => map.value?.leafletObject,
    (val) => {
        if (val) {
            nextTick(() => boundMap());
        }
    }
);

watch(
    () => props.rows,
    () => {
        if (map.value?.leafletObject) {
            nextTick(() => boundMap());
        }
    }
);
</script>
<template>
    <div class="MapView">
        <l-map
            ref="map"
            :class="['map', { border }]"
            :center="center"
            :zoom="zoom"
            :options="{
                mapTypeControl: false,
                scaleControl: false,
                streetViewControl: false,
                rotateControl: false,
                fullscreenControl: false,
                disableDefaultUi: false,
                clickableIcons: false,
            }"
        >
            <l-tile-layer :url="url" :attribution="attribution"></l-tile-layer>
        </l-map>
    </div>
</template>
<style lang="scss">
.MapView {
    &,
    .map {
        height: 100%;
        width: 100%;
        overflow: hidden;
    }
    .border {
        &,
        .map {
            border-radius: 8px;
        }
    }
}
</style>
